/**
 * Data Import Wizard — auto-map suggestion reconciliation (S6.T2b).
 *
 * Pure helper only: the WIRING (calling `data-import:target-table-columns`,
 * running this over the response's `suggestedMappings`, and `setMappings`-ing
 * the result into the store) is S6.T2c's concern (the Target-step destination-
 * commit flow). This function just answers "given the server's suggested
 * mapping and the CURRENT column plan, which suggestions still make sense?" —
 * a suggestion computed server-side against the file's columns can go stale by
 * the time it's applied if the user toggled a column's `include` off in the
 * interim, so a suggestion referencing an excluded source is dropped rather
 * than silently re-including that column via the mapping.
 *
 * Process-agnostic shared module (src/features/workspace/data-import/mapping/):
 * no React/zustand/DOM/electron-runtime imports, matching `columnMapping.ts`/
 * `targetEligibility.ts` in this folder.
 */
import type { ImportColumnPlan, ImportTargetMapping } from '../../../../types/electron';

/**
 * Drops any `suggested` entry whose `source.kind === 'file'` `sourceIndex`
 * does not resolve to a currently-`include: true` column (this also covers a
 * dangling index that matches no column at all) — `kind: 'fixed'` suggestions
 * (none exist yet, but the type allows them) always pass through unchanged.
 */
export function reconcileSuggestedMappings(
  suggested: ImportTargetMapping[],
  columns: ImportColumnPlan[],
): ImportTargetMapping[] {
  const includedIndexes = new Set(columns.filter((c) => c.include).map((c) => c.index));
  return suggested.filter((m) => m.source.kind !== 'file' || includedIndexes.has(m.source.sourceIndex));
}
